/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import { Editing, newMonthValues, splitToQuarter } from "../helpers";

type Props = {
  newBudget: number[][];
  quarterBudgets: number[][];
  growthRateBudgetCoefficient: number;
  setNewBudget: (value: number[][]) => void;
  setEditing: (value: boolean) => void;
  setGrowthRate: (value: number) => void;
  onEdit: (values: number[]) => Promise<void>;
  setShowApply: (value: boolean) => void;
  onReset: (cached: number[][]) => void;
};

export function useBudgetBreakdownActions({
  newBudget,
  quarterBudgets,
  growthRateBudgetCoefficient,
  setNewBudget,
  setEditing,
  setGrowthRate,
  onEdit,
  setShowApply,
  onReset,
}: Props) {
  const [lastEdited, setLastEdited] = React.useState<Editing>();
  const monthly = React.useMemo(() => quarterBudgets.flat(), [quarterBudgets]);
  const [cachedMonthlyBudget, setCachedMonthlyBudget] =
    React.useState(newBudget);

  const hasAnyChangedComparedToCache = React.useMemo(
    () =>
      newBudget
        .flat()
        .some((value, index) => value !== cachedMonthlyBudget.flat()[index]),
    [cachedMonthlyBudget, newBudget]
  );

  const updateBudget = (value = 0, index?: number) => {
    if (lastEdited === Editing.MONTHLY) {
      const newValues = splitToQuarter(
        newBudget.flat().map((item, i) => (i === index ? value : item))
      );
      setNewBudget(newValues);
    }
  };

  const cancel = () => {
    setNewBudget(cachedMonthlyBudget);
    setEditing(false);
    setGrowthRate(0);
    onReset(cachedMonthlyBudget);
  };

  const saveBudget = async () => {
    await onEdit(newBudget.flat());
    setEditing(false);
    setGrowthRate(0);
    setCachedMonthlyBudget(newBudget);
  };

  const hasChanged = (index: number) => {
    return monthly[index] !== newBudget.flat()[index];
  };

  const yearlyBudgetChanged = (value = 0) => {
    if (lastEdited === Editing.YEARLY) {
      setNewBudget(splitToQuarter(newMonthValues(value)));
      setShowApply(true);
    }
  };

  const applyGrowthRateBudget = () => {
    const afterGrowthBudget = splitToQuarter(
      newBudget
        .flat()
        .map((monthlyValue) =>
          Number((monthlyValue * growthRateBudgetCoefficient).toFixed(2))
        )
    );
    setCachedMonthlyBudget(newBudget);
    setNewBudget(afterGrowthBudget);
    setLastEdited(Editing.GROWTH_RATE);
    setShowApply(false);
    setGrowthRate(0);
  };

  return {
    updateBudget,
    cancel,
    saveBudget,
    hasChanged,
    yearlyBudgetChanged,
    applyGrowthRateBudget,
    setLastEdited,
    hasAnyChangedComparedToCache,
  };
}
